import React, { useEffect, useState } from 'react';
import { ActionButton } from './ActionButton';
import { Check } from 'lucide-react';
import { CARIBBEAN_BLUE, INK, MID_GRAY } from '@/styles/tokens';
import type { ActionButtonConfig } from './actionCardRegistry';
import { simpleDrilldowns, colorPropertyDrilldowns } from './actionCardRegistry';
import type { ColorSchemeWriteTarget } from '@/types/color-scheme';
import { DeviceProviderDrilldown } from './DeviceProviderDrilldown';
import type { Component } from '@/types';
import {
  TABLE_COLUMN_BREAKPOINTS,
  createResponsiveColumnVisibilityUpdate,
  getConfigurableTableColumns,
  getResponsiveVisibleColumnIds,
  getTableColumnKey,
  type TableColumnBreakpoint,
} from '@/components/tables/table-responsive-columns';

interface ActionCardShellProps {
  icon: string;
  title: string;
  summary?: string | null;
  buttons: ActionButtonConfig[];
  componentType?: string;
  /** Whether the component currently has a data source connected */
  hasDataSource?: boolean;
  onAutoSend: (command: string, styleWriteTarget?: ColorSchemeWriteTarget) => void;
  onPreFill: (text: string, styleWriteTarget?: ColorSchemeWriteTarget) => void;
  onDataDiscovery?: () => void;
  onRemoveData?: () => void;
  onIconPicker?: () => void;
  onMapTemplates?: () => void;
  onColorPicker?: (commandPrefix: string, propertyLabel: string, styleWriteTarget?: ColorSchemeWriteTarget) => void;
  onDeviceBinding?: () => void;
  onHomeAssistantBinding?: () => void;
  onHomeAssistantUnbind?: () => void;
  onHomeAssistantExplain?: () => void;
  onDuplicateComponent?: () => void;
  onHideComponent?: () => void;
  styleWriteTarget?: ColorSchemeWriteTarget;
  onStyleWriteTargetChange?: (target: ColorSchemeWriteTarget) => void;
  onVariantStyleEdit?: (commandOrPrefix: string) => void;
  component?: Component;
  updateComponent?: (id: string, updates: Partial<Component>) => void;
  onInjectMessage?: (content: string) => void;
  /** Override content rendered instead of the default button grid (used by Pin/Gauge cards) */
  children?: React.ReactNode;
}

interface ResponsiveColumnsDrilldownProps {
  component: Component;
  updateComponent: (id: string, updates: Partial<Component>) => void;
  onInjectMessage?: (content: string) => void;
  onBack: () => void;
}

const responsiveColumnsHeaderStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  margin: '8px 0',
};

const responsiveColumnsTitleStyle: React.CSSProperties = {
  fontSize: '12px',
  color: MID_GRAY.DEFAULT,
};

const responsiveColumnsBreakpointListStyle: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '6px',
  marginBottom: '8px',
};

const responsiveColumnsOptionsStyle: React.CSSProperties = {
  display: 'grid',
  gap: '6px',
  marginBottom: '8px',
};

const responsiveColumnsOptionBaseStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  minHeight: '44px',
  padding: '9px 10px',
  border: '1px solid rgba(221, 225, 232, 0.09)',
  borderRadius: '8px',
  backgroundColor: 'rgba(255, 255, 255, 0.025)',
  color: 'hsl(var(--foreground))',
  fontSize: '13px',
  transition: 'background-color 150ms ease, border-color 150ms ease, box-shadow 150ms ease, opacity 150ms ease',
  userSelect: 'none',
};

const responsiveColumnsEmptyStyle: React.CSSProperties = {
  fontSize: '12px',
  color: MID_GRAY.DEFAULT,
  margin: '8px 0',
};

const getResponsiveColumnsOptionStyle = (
  disableLastSelected: boolean,
  checked: boolean,
): React.CSSProperties => ({
  ...responsiveColumnsOptionBaseStyle,
  backgroundColor: checked ? 'rgba(28, 169, 204, 0.10)' : responsiveColumnsOptionBaseStyle.backgroundColor,
  borderColor: checked ? 'rgba(28, 169, 204, 0.38)' : 'rgba(221, 225, 232, 0.09)',
  boxShadow: checked ? 'inset 0 0 0 1px rgba(28, 169, 204, 0.08)' : undefined,
  cursor: disableLastSelected ? 'not-allowed' : 'pointer',
  opacity: disableLastSelected ? 0.65 : 1,
});

const responsiveColumnsHiddenInputStyle: React.CSSProperties = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  border: 0,
};

const responsiveColumnsLabelTextStyle: React.CSSProperties = {
  color: 'hsl(var(--foreground))',
  lineHeight: 1.25,
};

const getResponsiveColumnsCheckboxStyle = (
  checked: boolean,
  disabled: boolean,
  focused: boolean,
): React.CSSProperties => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '22px',
  height: '22px',
  flexShrink: 0,
  borderRadius: '7px',
  border: checked
    ? `1px solid ${CARIBBEAN_BLUE.light}`
    : `1px solid ${INK[400]}`,
  backgroundColor: checked ? CARIBBEAN_BLUE.light : 'rgba(20, 22, 28, 0.24)',
  color: checked ? INK[950] : 'transparent',
  boxShadow: focused
    ? `0 0 0 3px rgba(28, 169, 204, 0.22), 0 0 0 1px ${CARIBBEAN_BLUE.light}`
    : checked
    ? '0 0 0 3px rgba(28, 169, 204, 0.12)'
    : 'inset 0 0 0 1px rgba(255, 255, 255, 0.03)',
  transition: 'background-color 150ms ease, border-color 150ms ease, box-shadow 150ms ease, color 150ms ease',
  opacity: disabled ? 0.72 : 1,
});

const ResponsiveColumnsDrilldown: React.FC<ResponsiveColumnsDrilldownProps> = ({
  component,
  updateComponent,
  onInjectMessage,
  onBack,
}) => {
  const [activeBreakpoint, setActiveBreakpoint] =
    useState<TableColumnBreakpoint>('mobile');
  const [localVisibleIdsByBreakpoint, setLocalVisibleIdsByBreakpoint] =
    useState<Partial<Record<TableColumnBreakpoint, string[]>>>({});
  const [focusedColumnId, setFocusedColumnId] = useState<string | null>(null);
  const columns = getConfigurableTableColumns(component.properties);
  const columnIds = columns.map(getTableColumnKey);
  const explicitSelectedIds = getResponsiveVisibleColumnIds(component, activeBreakpoint);
  const selectedIds = localVisibleIdsByBreakpoint[activeBreakpoint] ?? explicitSelectedIds ?? columnIds;
  const selectedIdSet = new Set(selectedIds);

  useEffect(() => {
    setLocalVisibleIdsByBreakpoint({});
  }, [component.id]);

  const commitSelection = (visibleIds: string[]) => {
    setLocalVisibleIdsByBreakpoint((current) => ({
      ...current,
      [activeBreakpoint]: visibleIds,
    }));
    updateComponent(
      component.id,
      createResponsiveColumnVisibilityUpdate(
        component,
        activeBreakpoint,
        visibleIds,
      ),
    );
    onInjectMessage?.(
      `Updated ${activeBreakpoint} table columns for "${component.name || component.type}".`,
    );
  };

  const toggleColumn = (columnId: string) => {
    const nextSelected = selectedIdSet.has(columnId)
      ? selectedIds.filter((id) => id !== columnId)
      : columnIds.filter((id) => selectedIdSet.has(id) || id === columnId);

    if (nextSelected.length === 0) return;
    commitSelection(nextSelected);
  };

  const showAllColumns = () => {
    if (columnIds.length === 0) return;
    commitSelection(columnIds);
  };

  return (
    <>
      <div style={responsiveColumnsHeaderStyle}>
        <ActionButton variant="back" onClick={onBack}>
          ← Back
        </ActionButton>
        <span style={responsiveColumnsTitleStyle}>Columns</span>
      </div>
      <div style={responsiveColumnsBreakpointListStyle}>
        {TABLE_COLUMN_BREAKPOINTS.map((breakpoint) => (
          <ActionButton
            key={breakpoint.id}
            variant={breakpoint.id === activeBreakpoint ? 'active' : 'default'}
            onClick={() => setActiveBreakpoint(breakpoint.id)}
          >
            {breakpoint.label}
          </ActionButton>
        ))}
      </div>
      {columns.length > 0 ? (
        <>
          <div style={responsiveColumnsOptionsStyle}>
            {columns.map((column) => {
              const columnId = getTableColumnKey(column);
              const checked = selectedIdSet.has(columnId);
              const disableLastSelected = checked && selectedIds.length <= 1;

              return (
                <label
                  key={columnId}
                  style={getResponsiveColumnsOptionStyle(disableLastSelected, checked)}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled={disableLastSelected}
                    onChange={() => toggleColumn(columnId)}
                    onFocus={() => setFocusedColumnId(columnId)}
                    onBlur={() => setFocusedColumnId((current) => current === columnId ? null : current)}
                    style={responsiveColumnsHiddenInputStyle}
                  />
                  <span
                    aria-hidden="true"
                    style={getResponsiveColumnsCheckboxStyle(checked, disableLastSelected, focusedColumnId === columnId)}
                  >
                    <Check size={16} strokeWidth={3} />
                  </span>
                  <span style={responsiveColumnsLabelTextStyle}>{column.header}</span>
                </label>
              );
            })}
          </div>
          <ActionButton variant="dashed" onClick={showAllColumns}>
            Show All
          </ActionButton>
        </>
      ) : (
        <p style={responsiveColumnsEmptyStyle}>
          No columns configured.
        </p>
      )}
    </>
  );
};

const getDataAdditionsToggleLabel = (component?: Component): string =>
  component?.properties.dataAdditionsEnabled === true
    ? 'Disable Data Add'
    : 'Enable Data Add';

export const ActionCardShell: React.FC<ActionCardShellProps> = ({
  icon,
  title,
  summary,
  buttons,
  componentType,
  hasDataSource,
  onAutoSend,
  onPreFill,
  onDataDiscovery,
  onRemoveData,
  onIconPicker,
  onMapTemplates,
  onColorPicker,
  onDeviceBinding,
  onHomeAssistantBinding,
  onHomeAssistantUnbind,
  onHomeAssistantExplain,
  onDuplicateComponent,
  onHideComponent,
  styleWriteTarget = 'both',
  onStyleWriteTargetChange,
  onVariantStyleEdit,
  component,
  updateComponent,
  onInjectMessage,
  children,
}) => {
  const [expandedDrilldown, setExpandedDrilldown] = useState<string | null>(null);

  const toggleDataAdditions = () => {
    if (!component || !updateComponent) return;

    const nextEnabled = component.properties.dataAdditionsEnabled !== true;
    updateComponent(component.id, {
      properties: {
        ...component.properties,
        dataAdditionsEnabled: nextEnabled,
      },
    });
    onInjectMessage?.(
      `${nextEnabled ? 'Enabled' : 'Disabled'} table data add for "${component.name || component.type}".`,
    );
  };

  // If children are provided, render them directly (for Pin/Gauge cards)
  if (children) {
    return <>{children}</>;
  }

  // Hide "Remove Data" when there's no data source connected
  const visibleButtons = buttons
    .filter((btn) => btn.action !== 'removeData' || hasDataSource)
    .map((btn) =>
      btn.action === 'dataAdditionsToggle'
        ? { ...btn, label: getDataAdditionsToggleLabel(component) }
        : btn,
    );

  const isColorDrilldown = expandedDrilldown === '__color';
  const isDeviceProviderDrilldown = expandedDrilldown === '__deviceProvider';
  const isResponsiveColumnsDrilldown = expandedDrilldown === '__responsiveColumns';
  const activeDrilldownConfig = expandedDrilldown && !isColorDrilldown && !isDeviceProviderDrilldown
    ? simpleDrilldowns[expandedDrilldown]
    : null;
  const activeDrilldown = activeDrilldownConfig?.options ?? null;
  const customPrefix = activeDrilldownConfig?.customPrefix;
  const colorOptions = componentType ? colorPropertyDrilldowns[componentType] : undefined;
  const showStyleWriteTarget = onStyleWriteTargetChange && !isResponsiveColumnsDrilldown;

  const openDeviceBindingFlow = () => {
    if (onHomeAssistantBinding && onDeviceBinding) {
      setExpandedDrilldown('__deviceProvider');
      return;
    }
    if (onHomeAssistantBinding) {
      onHomeAssistantBinding();
      return;
    }
    onDeviceBinding?.();
  };

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
        <p style={{ fontSize: '14px', margin: 0, lineHeight: 1.5 }}>
          {icon} <strong>{title}</strong>
        </p>
        {showStyleWriteTarget && (
          <div
            role="group"
            aria-label="Style write target"
            style={{
              display: 'inline-flex',
              border: '1px solid hsl(var(--border))',
              borderRadius: '6px',
              overflow: 'hidden',
              flexShrink: 0,
            }}
          >
            {(['both', 'dark', 'light'] as const).map((target) => {
              const active = styleWriteTarget === target;
              return (
                <button
                  key={target}
                  type="button"
                  aria-pressed={active}
                  onClick={() => onStyleWriteTargetChange(target)}
                  style={{
                    border: 'none',
                    borderRight: target === 'light' ? 'none' : '1px solid hsl(var(--border))',
                    background: active ? 'hsl(var(--primary))' : 'transparent',
                    color: active ? 'hsl(var(--primary-foreground))' : 'hsl(var(--muted-foreground))',
                    cursor: 'pointer',
                    fontSize: '11px',
                    fontWeight: active ? 600 : 500,
                    padding: '3px 7px',
                    textTransform: 'capitalize',
                  }}
                >
                  {target}
                </button>
              );
            })}
          </div>
        )}
      </div>
      {summary && (
        <p style={{ fontSize: '12px', margin: '4px 0 8px', color: MID_GRAY.DEFAULT }}>
          {summary}
        </p>
      )}
      {isDeviceProviderDrilldown ? (
        <DeviceProviderDrilldown
          onBack={() => setExpandedDrilldown(null)}
          onHomeAssistantBinding={onHomeAssistantBinding}
          onDeviceBinding={onDeviceBinding}
        />
      ) : isColorDrilldown && colorOptions ? (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: '8px 0' }}>
            <ActionButton variant="back" onClick={() => setExpandedDrilldown(null)}>
              ← Back
            </ActionButton>
            <span style={{ fontSize: '12px', color: MID_GRAY.DEFAULT }}>
              Pick a color property
            </span>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {colorOptions.map((option) => (
              <ActionButton
                key={option.label}
                onClick={() => {
                  setExpandedDrilldown(null);
                  if (styleWriteTarget !== 'both') onVariantStyleEdit?.(option.commandPrefix);
                  onColorPicker?.(option.commandPrefix, option.label, styleWriteTarget);
                }}
              >
                {option.label}
              </ActionButton>
            ))}
          </div>
        </>
      ) : isResponsiveColumnsDrilldown && component && updateComponent ? (
        <ResponsiveColumnsDrilldown
          component={component}
          updateComponent={updateComponent}
          onInjectMessage={onInjectMessage}
          onBack={() => setExpandedDrilldown(null)}
        />
      ) : activeDrilldown ? (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: '8px 0' }}>
            <ActionButton variant="back" onClick={() => setExpandedDrilldown(null)}>
              ← Back
            </ActionButton>
            <span style={{ fontSize: '12px', color: MID_GRAY.DEFAULT }}>
              {buttons.find(b => b.drilldownId === expandedDrilldown)?.label}
            </span>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {activeDrilldown.map((option) => (
              <ActionButton
                key={option.label}
                onClick={() => {
                  if (styleWriteTarget !== 'both') onVariantStyleEdit?.(option.command);
                  onAutoSend(option.command, styleWriteTarget);
                  setExpandedDrilldown(null);
                }}
              >
                {option.label}
              </ActionButton>
            ))}
            {customPrefix && (
              <ActionButton
                variant="dashed"
                onClick={() => {
                  if (styleWriteTarget !== 'both') onVariantStyleEdit?.(customPrefix);
                  onPreFill(customPrefix, styleWriteTarget);
                  setExpandedDrilldown(null);
                }}
              >
                Custom...
              </ActionButton>
            )}
          </div>
        </>
      ) : (
        <>
          {!summary && <p style={{ fontSize: '13px', margin: '8px 0', color: MID_GRAY.DEFAULT }}>Quick actions:</p>}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: summary ? 0 : undefined }}>
            {visibleButtons.map((btn) => (
              <ActionButton
                key={btn.label}
                variant={btn.action === 'removeData' || btn.action === 'homeAssistantUnbind' || btn.label === 'Delete' ? 'destructive' : undefined}
                onClick={() => {
                  if (btn.action === 'autoSend' && btn.command) {
                    if (styleWriteTarget !== 'both') onVariantStyleEdit?.(btn.command);
                    onAutoSend(btn.command, styleWriteTarget);
                  } else if (btn.action === 'preFill' && btn.command) {
                    if (styleWriteTarget !== 'both') onVariantStyleEdit?.(btn.command);
                    onPreFill(btn.command, styleWriteTarget);
                  } else if (btn.action === 'drilldown' && btn.drilldownId) {
                    setExpandedDrilldown(btn.drilldownId);
                  } else if (btn.action === 'responsiveColumns') {
                    setExpandedDrilldown('__responsiveColumns');
                  } else if (btn.action === 'dataAdditionsToggle') {
                    toggleDataAdditions();
                  } else if (btn.action === 'dataDiscovery' && onDataDiscovery) {
                    onDataDiscovery();
                  } else if (btn.action === 'removeData' && onRemoveData) {
                    onRemoveData();
                  } else if (btn.action === 'iconPicker' && onIconPicker) {
                    onIconPicker();
                  } else if (btn.action === 'mapTemplates' && onMapTemplates) {
                    onMapTemplates();
                  } else if (btn.action === 'deviceBinding') {
                    openDeviceBindingFlow();
                  } else if (btn.action === 'homeAssistantBinding' && onHomeAssistantBinding) {
                    onHomeAssistantBinding();
                  } else if (btn.action === 'homeAssistantUnbind' && onHomeAssistantUnbind) {
                    onHomeAssistantUnbind();
                  } else if (btn.action === 'homeAssistantExplain' && onHomeAssistantExplain) {
                    onHomeAssistantExplain();
                  } else if (btn.action === 'duplicateComponent' && onDuplicateComponent) {
                    onDuplicateComponent();
                  } else if (btn.action === 'hideComponent' && onHideComponent) {
                    onHideComponent();
                  } else if (btn.action === 'colorPicker' && onColorPicker) {
                    // If multiple color properties, show sub-menu; if single, open modal directly
                    if (colorOptions && colorOptions.length > 1) {
                      setExpandedDrilldown('__color');
                    } else if (colorOptions && colorOptions.length === 1) {
                      if (styleWriteTarget !== 'both') onVariantStyleEdit?.(colorOptions[0].commandPrefix);
                      onColorPicker(colorOptions[0].commandPrefix, colorOptions[0].label, styleWriteTarget);
                    } else {
                      if (styleWriteTarget !== 'both') onVariantStyleEdit?.('set color to ');
                      onColorPicker('set color to ', 'Color', styleWriteTarget);
                    }
                  }
                }}
              >
                {btn.label}
              </ActionButton>
            ))}
          </div>
        </>
      )}
    </>
  );
};
